import { configureStore } from "@reduxjs/toolkit";
import { createBrowserHistory } from "history";
import { routerMiddleware } from "connected-react-router";
import { createEpicMiddleware } from "redux-observable";
import createRootReducer from "./reducers";
import rootEpic from "./actions/epics";
import batchedSubscribeMiddleware from "./batching/middleware";
import batchedSubscribeEnhancer from "./batching/enhancer";

const defaultState = {
    /*
    fields: {}, 
    modals:
    userId:
    companyId:
    */
};

// Figure out if we're on a single-page-app
const createHistoryOptions = {};
const pathName = window.location && window.location.pathname ? window.location.pathname : "";
if (pathName.toLowerCase() === "/de" || pathName.match(/^\/de\//i)) {
    createHistoryOptions["basename"] = "/de";
}
if (pathName.toLowerCase() === "/ae" || pathName.match(/^\/ae\//i)) {
    createHistoryOptions["basename"] = "/ae";
}

const history = createBrowserHistory(createHistoryOptions);
export { history };

const epicMiddleware = createEpicMiddleware();

// Add debounce redux subscription notifications [middleware]
const middleware = [batchedSubscribeMiddleware, epicMiddleware, routerMiddleware(history)];

// Uncomment the next two lines to log all actions to console
// import logger from "redux-logger";
// middleware.push(logger);
// Localstorage via redux-persist and localforage

// Using debounced subscription notifications (enhancer)
const store = configureStore({
    reducer: createRootReducer(history),
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            immutableCheck: false,
            serializableCheck: false,
        }).concat(middleware),
    preloadedState: defaultState,
    enhancers: getDefaultEnhancers =>
        getDefaultEnhancers({
            autoBatch: { type: "tick" },
        }).concat(batchedSubscribeEnhancer),
});

epicMiddleware.run(rootEpic);

// Initialize globals now that store exists
top.store = store;
top.RDX = top.RDX || {};

const { updateFlowList, clearSelectedFlow } = require("./actions/flowManagementActions");

const {
    requestFlowItem,
    requestFlows,
    checkFlowPermission,
    requestFlow,
    requestFlowObjectsFromCloud,
    getFlowItemDeployStatuses,
    requestFlowDeploymentQueue,
    requestFlowCountLog,
    requestFlowExportApproval,
    requestFlowCountApproval,
    deployFlowExport,
    cancelFlowExportRequest,
    decideFlowExport,
    decideCountApproval,
    requestFinalizeDiscoveries,
    duplicateFlowItem,
    flowItemCopyToIdZero,
    flowItemCopyToIdZeroBuffer,
    removeFlowDimensionField,
    removeFlowMeasureField,
    addFlowDimensionField,
    addFlowDimensionObject,
    flowReportAddMeasureField,
    addFlowDimensionMeasureField,
    addFlowDimensionToCannedReport,
    removeFlowDimensionMeasureField,
    removeFlowMeasureFieldByIndex,
    removeFlowDimensionFromCannedReport,
    flowCopyToIdZero,
    flowCopyToIdZeroFromBuffer,
} = require("./actions/flowActions");

const {
    setPage,
    siteCustomization,
    setPremiumFields,
    requestPremiumFields,
    setVar,
    setDataSource,
    clearDataSource,
    requestQueryBuilderGroupList,
    updateQueryBuilderGroupList,
    showModal,
    hideModal,
} = require("./actions/actionCreators");

const { addNotifyMessageTop } = require("./actions/notifyActions");
const { dispatchGetFileMasterLog } = require("./actions/dashboardWidgetActions");

// lodash utilities (runtime require keeps top import list lean)
const memoize = require("lodash/memoize");
const debounce = require("lodash/debounce");

// Simple local wrap matching original usage (lodash/wrap not suitable here)
const wrap =
    (factoryFn, invoke) =>
    (...args) =>
        invoke(factoryFn, ...args);

// Attach helpers
top.RDX.addNotifyMessage = addNotifyMessageTop;

top.RDX.updateFlowList = flows => top.store.dispatch(updateFlowList(flows));
top.RDX.clearSelectedFlow = () => top.store.dispatch(clearSelectedFlow());
top.RDX.requestFlowItem = flowItemId => top.store.dispatch(requestFlowItem(flowItemId));

// Special case for RDX.requestFlows.  Because this is called whenever a flowItem completes (via NotifyChange in C#),
// and often, several flowItems finish right after each other (splits, for example), we will debounce this quite heavily
// in order to reduce the amount of needless http requests and processing done on the client.
const _requestFlowsDispatch = (flowId, mergeBehavior) => top.store.dispatch(requestFlows(flowId, mergeBehavior));
top.RDX.requestFlows = wrap(
    memoize(
        () => debounce(_requestFlowsDispatch, 1100),
        flowId => flowId
    ),
    (fn, flowId, mergeBehavior) => fn(flowId)(flowId, mergeBehavior)
);

top.RDX.checkFlowPermission = flowId => top.store.dispatch(checkFlowPermission(flowId));

const _requestFlowDispatch = (flowId, mergeBehavior) => top.store.dispatch(requestFlow(flowId, mergeBehavior));
top.RDX.requestFlow = wrap(
    memoize(
        () => debounce(_requestFlowDispatch, 1100),
        flowId => flowId
    ),
    (fn, flowId, mergeBehavior) => fn(flowId)(flowId, mergeBehavior)
);

// Flow related direct dispatchers (removed TS type annotations)
top.RDX.getFlowItemDeployStatuses = (flowItemId, companyId) =>
    top.store.dispatch(getFlowItemDeployStatuses(flowItemId, companyId));
top.RDX.requestFlowDeploymentQueue = (flowLogType, flowId, companyId) =>
    top.store.dispatch(requestFlowDeploymentQueue(flowLogType, flowId, companyId));
top.RDX.requestFlowCountLog = flowLogType => top.store.dispatch(requestFlowCountLog(flowLogType));
top.RDX.requestFlowObjectsFromCloud = () => top.store.dispatch(requestFlowObjectsFromCloud());
top.RDX.requestFlowExportApproval = flowId => top.store.dispatch(requestFlowExportApproval(flowId));
top.RDX.requestFlowCountApproval = flowId => top.store.dispatch(requestFlowCountApproval(flowId));
top.RDX.deployFlowExport = flowId => top.store.dispatch(deployFlowExport(flowId));
top.RDX.cancelFlowExportRequest = flowId => top.store.dispatch(cancelFlowExportRequest(flowId));
top.RDX.decideFlowExport = (workflowId, actionName, reason) =>
    top.store.dispatch(decideFlowExport(workflowId, actionName, reason));
top.RDX.decideCountApproval = (workflowId, actionName, flowItemId, flowItemType) =>
    top.store.dispatch(decideCountApproval(workflowId, actionName, flowItemId, flowItemType));
top.RDX.requestFinalizeDiscoveries = flowId => top.store.dispatch(requestFinalizeDiscoveries(flowId));

top.RDX.duplicateFlowItem = (flowItemId, newX, newY) => top.store.dispatch(duplicateFlowItem(flowItemId, newX, newY));
top.RDX.flowItemCopyToIdZero = (flowItemId, newFlowId) =>
    top.store.dispatch(flowItemCopyToIdZero(flowItemId, newFlowId));
top.RDX.flowItemCopyToIdZeroBuffer = (flowItemId, newFlowId) =>
    top.store.dispatch(flowItemCopyToIdZeroBuffer(flowItemId, newFlowId));

top.RDX.removeFlowDimensionField = (fieldKey, flowReportId, isExportReport) =>
    top.store.dispatch(removeFlowDimensionField(fieldKey, flowReportId, isExportReport));
top.RDX.removeFlowDimensionMeasureField = (fieldKey, flowReportId, isExportReport, flowItemId) =>
    top.store.dispatch(removeFlowDimensionMeasureField(fieldKey, flowReportId, isExportReport, flowItemId));
top.RDX.removeFlowDimensionFromCannedReport = (fieldKey, flowReportId) =>
    top.store.dispatch(removeFlowDimensionFromCannedReport(fieldKey, flowReportId));
top.RDX.removeFlowMeasureField = (fieldKey, flowReportId, isExportReport) =>
    top.store.dispatch(removeFlowMeasureField(fieldKey, flowReportId, isExportReport));
top.RDX.removeFlowMeasureFieldByIndex = (fieldKey, flowReportId, isExportReport, flowItemId) =>
    top.store.dispatch(removeFlowMeasureFieldByIndex(fieldKey, flowReportId, isExportReport, flowItemId));
top.RDX.addFlowDimensionField = (fieldKey, flowReportId) =>
    top.store.dispatch(addFlowDimensionField(fieldKey, flowReportId));
top.RDX.addFlowDimensionObject = (fieldKey, flowExportReportId) =>
    top.store.dispatch(addFlowDimensionObject(fieldKey, flowExportReportId));
top.RDX.flowReportAddMeasureField = (fieldKey, flowReportId, isExportReport, flowItemId) =>
    top.store.dispatch(flowReportAddMeasureField(fieldKey, flowReportId, isExportReport, flowItemId));
top.RDX.addFlowDimensionMeasureField = (fieldKey, flowReportId, isExportReport, flowItemId) =>
    top.store.dispatch(addFlowDimensionMeasureField(fieldKey, flowReportId, isExportReport, flowItemId));
top.RDX.addFlowDimensionToCannedReport = (field, flowReportId, flowItemId) =>
    top.store.dispatch(addFlowDimensionToCannedReport(field, flowReportId, flowItemId));
top.RDX.flowCopyToIdZero = (flowId, newOwnerId, copyOfferCodes, copyVariables) =>
    top.store.dispatch(flowCopyToIdZero(flowId, newOwnerId, copyOfferCodes, copyVariables));
top.RDX.flowCopyToIdZeroFromBuffer = (
    flowId,
    newOwnerId,
    clipboardNodes,
    clipboardRelations,
    pasteOffsetX,
    pasteOffsetY
) =>
    top.store.dispatch(
        flowCopyToIdZeroFromBuffer(flowId, newOwnerId, clipboardNodes, clipboardRelations, pasteOffsetX, pasteOffsetY)
    );

top.RDX.getFileMasterLog = () => dispatchGetFileMasterLog(top.store.dispatch);

// Generic actions
top.RDX.setPage = page => top.store.dispatch(setPage(page));
top.RDX.siteCustomization = (custName, defaultValue) => top.store.dispatch(siteCustomization(custName, defaultValue));
top.RDX.setPremiumFields = premiumFields => top.store.dispatch(setPremiumFields(premiumFields));
top.RDX.requestPremiumFields = () => top.store.dispatch(requestPremiumFields());
top.RDX.setVar = (variable, value) => top.store.dispatch(setVar(variable, value));
top.RDX.setDataSource = dataSource => top.store.dispatch(setDataSource(dataSource));
top.RDX.clearDataSource = () => top.store.dispatch(clearDataSource());
top.RDX.requestQueryBuilderGroupList = (segmentKey, groupType) =>
    top.store.dispatch(requestQueryBuilderGroupList(segmentKey, groupType));
top.RDX.updateQueryBuilderGroupList = groups => top.store.dispatch(updateQueryBuilderGroupList(groups));
top.RDX.showModal = (modalType, modalProps = {}) => top.store.dispatch(showModal(modalType, modalProps));
top.RDX.hideModal = modalType => top.store.dispatch(hideModal(modalType));

// This is a bit of a hack to load session information (IE, am I logged in) as soon as possible
// before apps start rendering even
// Needs this in the layout file:
// top.SessionInfo = @Html.Raw(Json.Encode( OmniActivation.Lib.Util.AllSessionInformation() ));
const { setSessionInfo } = require("./actions/sessionActions");
if (top.SessionInfo) {
    store.dispatch(setSessionInfo(top.SessionInfo));
}

// DataEngine: Always turn on 'tab navigation'
// import { setVar } from "./actions/actionCreators";

// Channels2: Always turn on splash page on first load
// REMOVE this (causes circular import under Jest):
// store.dispatch(setVar("isShowingChannels2Splash", true));

// Safe bootstrap helper (delays dispatch until after all modules loaded)
export const bootstrapStore = () => {
    store.dispatch({ type: "SET_VAR", variable: "isShowingChannels2Splash", value: true });
};

// Auto-run only in browser (skip during tests to avoid setVar undefined)
if (typeof window !== "undefined" && process.env.NODE_ENV !== "test") {
    bootstrapStore();
}

/* eslint-disable no-undef */
if (process != null && process.env.NODE_ENV === "development" && module.hot) {
    module.hot.accept("./reducers", () => {
        const newCreateRootReducer = require("./reducers").default;
        store.replaceReducer(newCreateRootReducer(history));
    });
}
/* eslint-enable no-undef */

export default store;
